<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">📊</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
        <template slot-scope="{ row }" slot="totalPowerOnSeconds">
          <span class="data-cell">{{ convertSecondsToHours(row.totalPowerOnSeconds) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="currentPowerOnSeconds">
          <span class="data-cell">{{ convertSecondsToHours(row.currentPowerOnSeconds) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="totalDrivingDistance">
          <span class="data-cell distance">{{ formatRawNumber(row.totalDrivingDistance) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="currentDrivingDistance">
          <span class="data-cell distance">{{ formatRawNumber(row.currentDrivingDistance) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="totalPowerOnTimes">
          <span class="data-cell">{{ formatNumber(row.totalPowerOnTimes) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="totalPowerOnDays">
          <span class="data-cell">{{ row.totalPowerOnDays }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="createTime">
          <span class="time-cell">{{ formatDateTime(row.createTime) }}</span>
        </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'

export default {
  name: 'MileageHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    columns() {
      return [
        {
          title: langMap.dashboard_totalPowerOnSeconds[this.lang],
          slot: 'totalPowerOnSeconds',
          align: 'center'
        },
        {
          title: langMap.dashboard_currentPowerOnSeconds[this.lang],
          slot: 'currentPowerOnSeconds',
          align: 'center'
        },
        {
          title: langMap.dashboard_totalDrivingDistance[this.lang],
          slot: 'totalDrivingDistance',
          align: 'center'
        },
        {
          title: langMap.dashboard_currentDrivingDistance[this.lang],
          slot: 'currentDrivingDistance',
          align: 'center'
        },
        {
          title: langMap.dashboard_totalPowerOnTimes[this.lang],
          slot: 'totalPowerOnTimes',
          align: 'center'
        },
        {
          title: langMap.dashboard_totalPowerOnDays[this.lang],
          slot: 'totalPowerOnDays',
          align: 'center'
        },
        {
          title: langMap.dashboard_createTime[this.lang],
          slot: 'createTime',
          align: 'center'
        }
      ]
    },
    totalPages() {
      return this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.size) || 1
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total)
      return `${start}-${end}`
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.pagination.current
      const delta = 2 // 显示当前页面前后各几页
      const range = []
      const rangeWithDots = []
      let l

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    
    // 格式化数字
    formatNumber(number) {
      if (!number && number !== 0) return '0'
      
      // 处理字符串格式的数字
      const num = parseInt(number)
      if (isNaN(num)) return '0'
      
      return num.toString()
    },
    // 格式化原始数值（不转换单位,直接显示)
    formatRawNumber(number) {
      if (!number && number !== 0) return '-'
      
      // 处理字符串格式的数字
      const num = parseFloat(number)
      if (isNaN(num)) return '-'
      
      // 直接返回数值,保留小数点(如果有)
      return num.toString()
    },
    
    // 将秒转换为小时显示
    convertSecondsToHours(seconds) {
      if (!seconds && seconds !== 0) return '-'
      
      const num = parseFloat(seconds)
      if (isNaN(num)) return '-'
      
      // 转换为小时并保留2位小数
      const hours = (num / 3600).toFixed(2)
      return `${hours}h`
    },
    
    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      
      // 如果已经是格式化后的字符串，直接返回
      if (typeof timestamp === 'string' && timestamp.includes('-')) {
        return timestamp
      }
      
      // 否则将时间戳转换为日期格式
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },
    
    // 计算表格高度
    calculateTableHeight() {
      const windowHeight = window.innerHeight
      const headerHeight = 120 // 查询栏高度
      const paginationHeight = 60 // 分页高度
      const padding = 40 // 上下边距
      this.tableHeight = windowHeight - headerHeight - paginationHeight - padding - 281
      if (this.tableHeight < 300) {
        this.tableHeight = 300
      }
    },
    
    // 分页处理
    handlePageChange(page) {
      this.$emit('page-change', page)
    },
    
    handlePageSizeChange(size) {
      this.$emit('size-change', size)
    },
    
    // 快速跳转页面
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(
          langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages)
        )
        this.jumpPage = null
        return
      }
      this.handlePageChange(this.jumpPage)
      this.jumpPage = null
    }
  }
}
</script>

<style lang="less" scoped>
// 数据单元格样式
.data-cell {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #00f0ff;
  text-shadow: 0 0 5px rgba(0, 240, 255, 0.6);
  
  &.distance {
    color: #39FF14;
    text-shadow: 0 0 5px rgba(57, 255, 20, 0.6);
  }
}

.time-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}
</style>